import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useDropzone } from 'react-dropzone';
import { customerAPI, agentAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import SetupCompleteModal from '../../components/admin/SetupCompleteModal';
import toast from 'react-hot-toast';
import {
  CheckIcon,
  LockClosedIcon,
  GlobeAltIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  PlusIcon,
  TrashIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';

const TOTAL_STEPS = 4;

// Smart Assistant personality options — kept in sync with the field set the
// agent-config endpoint exposes. custom_instructions and greeting_message
// are NOT shown during onboarding (handled later via Profile or via the
// backend's defaults).
const PERSONALITY_TRAITS = [
  { value: 'friendly', label: 'Friendly & Approachable' },
  { value: 'professional', label: 'Professional & Polished' },
  { value: 'empathetic', label: 'Empathetic & Understanding' },
  { value: 'technical', label: 'Technical & Precise' },
  { value: 'formal', label: 'Formal & Structured' },
  { value: 'enthusiastic', label: 'Enthusiastic & Energetic' },
  { value: 'casual', label: 'Casual & Relaxed' },
  { value: 'patient', label: 'Patient & Thorough' },
  { value: 'direct', label: 'Direct & Concise' },
  { value: 'creative', label: 'Creative & Innovative' },
  { value: 'analytical', label: 'Analytical & Logical' },
  { value: 'supportive', label: 'Supportive & Encouraging' },
  { value: 'energetic', label: 'Energetic & Dynamic' },
  { value: 'calm', label: 'Calm & Composed' },
];

const RESPONSE_STYLES = [
  { value: 'conversational', label: 'Conversational' },
  { value: 'technical', label: 'Technical' },
  { value: 'casual', label: 'Casual' },
  { value: 'formal', label: 'Formal' },
];

// Step indicator component
function StepIndicator({ currentStep }) {
  const steps = Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1);
  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={step}>
          <div className="flex flex-col items-center">
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors ${
                step < currentStep
                  ? 'bg-cleona-600 border-cleona-600 text-white'
                  : step === currentStep
                  ? 'bg-cleona-600 border-cleona-600 text-white'
                  : 'bg-white border-gray-300 text-gray-400'
              }`}
            >
              {step < currentStep ? (
                <CheckIcon className="h-5 w-5" />
              ) : step === currentStep ? (
                <CheckIcon className="h-5 w-5" />
              ) : (
                <LockClosedIcon className="h-4 w-4" />
              )}
            </div>
            <span className="text-xs text-gray-500 mt-2">Step {step}</span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`flex-1 h-0.5 mx-4 transition-colors ${
                step < currentStep ? 'bg-cleona-600' : 'bg-gray-200'
              }`}
              style={{ minWidth: '60px', maxWidth: '160px' }}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function AdminProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const { data: profile, isLoading } = useQuery('profile', customerAPI.getProfile);

  // Step 1: Company Information
  const [companyData, setCompanyData] = useState({
    company_name: '',
    company_website: '',
    contact_email: '',
    contact_name: '',
  });

  // Step 2: Smart Assistant Settings — prefilled from agentAPI.getConfig
  // which auto-creates a row with sensible defaults on first read.
  const [agentSettings, setAgentSettings] = useState({
    agent_name: '',
    agent_role: '',
    personality_traits: [],
    response_style: 'conversational',
  });
  // Preserve fields the onboarding step doesn't show (greeting_message,
  // industry, custom_instructions, system_prompt) so saving doesn't blow
  // them away if the tenant set them elsewhere.
  const [agentPassthrough, setAgentPassthrough] = useState({});
  const [agentTouched, setAgentTouched] = useState(false);

  // Step 3: Avatar
  const [avatar, setAvatar] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);

  // Step 4: Widget Security
  const [domainMode, setDomainMode] = useState('unrestricted'); // 'unrestricted' or 'specific'
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');

  useEffect(() => {
    if (profile) {
      setCompanyData({
        company_name: profile.company_name || '',
        company_website: profile.company_website || '',
        contact_email: profile.contact_email || '',
        contact_name: profile.contact_name || '',
      });

      const allowedDomains = profile.allowed_domains || [];
      if (allowedDomains.includes('*') || allowedDomains.length === 0) {
        setDomainMode('unrestricted');
        setDomains([]);
      } else {
        setDomainMode('specific');
        setDomains(allowedDomains);
      }

      if (profile.avatar_url) {
        setAvatarPreview(profile.avatar_url);
      }
    }
  }, [profile]);

  // Load the agent config in parallel with the profile. Backend
  // auto-creates a row with sensible defaults on first GET (see
  // cleona-chat-service/app/routers/agent.py), so this always populates
  // the form with valid starting values.
  const { data: agentConfig } = useQuery('agentConfig', agentAPI.getConfig);

  useEffect(() => {
    if (!agentConfig) return;
    setAgentSettings({
      agent_name: agentConfig.agent_name || '',
      agent_role: agentConfig.agent_role || '',
      personality_traits: agentConfig.personality_traits || [],
      response_style: agentConfig.response_style || 'conversational',
    });
    setAgentPassthrough({
      greeting_message: agentConfig.greeting_message || '',
      custom_instructions: agentConfig.custom_instructions || '',
      industry: agentConfig.industry || 'general',
      system_prompt: agentConfig.system_prompt || '',
    });
  }, [agentConfig]);

  const updateMutation = useMutation(
    (data) => customerAPI.updateProfile(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('profile');
        queryClient.invalidateQueries('dashboard');
        setShowSuccessModal(true);
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Failed to update profile');
      },
    }
  );

  const agentMutation = useMutation(
    (data) => agentAPI.updateConfig(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('agentConfig');
      },
      onError: (error) => {
        // Non-fatal: profile save can still succeed even if agent config
        // failed. Surface a toast so the tenant knows to retry.
        toast.error(
          error.response?.data?.detail || 'Failed to save Smart Assistant settings'
        );
      },
    }
  );

  const togglePersonalityTrait = (traitValue) => {
    setAgentTouched(true);
    setAgentSettings((prev) => {
      const traits = prev.personality_traits;
      if (traits.includes(traitValue)) {
        return { ...prev, personality_traits: traits.filter((t) => t !== traitValue) };
      } else if (traits.length < 5) {
        return { ...prev, personality_traits: [...traits, traitValue] };
      }
      return prev;
    });
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setAvatar(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    maxSize: 5 * 1024 * 1024,
    multiple: false,
  });

  const handleAddDomain = () => {
    if (newDomain.trim() && !domains.includes(newDomain.trim())) {
      setDomains([...domains, newDomain.trim()]);
      setNewDomain('');
    }
  };

  const handleRemoveDomain = (domain) => {
    setDomains(domains.filter(d => d !== domain));
  };

  const handleNext = () => {
    if (currentStep === 1) {
      if (!companyData.company_name || !companyData.contact_email) {
        toast.error('Please fill in required fields');
        return;
      }
    }
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    // Persist agent settings only if the tenant touched them. If they
    // didn't, the backend already has either the auto-created defaults
    // or whatever the tenant previously saved — no point overwriting.
    if (agentTouched) {
      agentMutation.mutate({ ...agentPassthrough, ...agentSettings });
    }

    // Resolve allowed_domains from the simplified Step 4 UX:
    //   - "Use Website Chat only here" → domainMode='specific', domains=[]  → save []
    //   - "Put chat on a website" with no restrictions → save ['*']
    //   - Either choice with restrictions added in the disclosure → save the domain list
    const allowed_domains =
      domains.length > 0
        ? domains
        : domainMode === 'specific'
          ? []
          : ['*'];

    const data = {
      ...companyData,
      allowed_domains,
    };

    updateMutation.mutate(data);
  };

  const handleSkip = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cleona-600"></div>
      </div>
    );
  }

  return (
    <div className="font-source">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 font-arsenal">Complete Your Setup</h1>
        <p className="mt-1 text-gray-600">
          Tell us about your company to get started with your AI assistant.
        </p>
      </div>

      {/* Card */}
      <div className="bg-white rounded-xl border border-gray-100 p-8">
        {/* Step Indicator */}
        <StepIndicator currentStep={currentStep} />

        {/* Step 1: Company Information */}
        {currentStep === 1 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Company Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name
                </label>
                <input
                  type="text"
                  value={companyData.company_name}
                  onChange={(e) => setCompanyData({ ...companyData, company_name: e.target.value })}
                  placeholder="Your Company"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Company Website
                </label>
                <input
                  type="url"
                  value={companyData.company_website}
                  onChange={(e) => setCompanyData({ ...companyData, company_website: e.target.value })}
                  placeholder="Your Link"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  value={companyData.contact_email}
                  onChange={(e) => setCompanyData({ ...companyData, contact_email: e.target.value })}
                  placeholder="admin@company.com"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person
                </label>
                <input
                  type="text"
                  value={companyData.contact_name}
                  onChange={(e) => setCompanyData({ ...companyData, contact_name: e.target.value })}
                  placeholder="Your Contact"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Smart Assistant Settings */}
        {currentStep === 2 && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-semibold text-gray-900">Smart Assistant&trade; Settings</h2>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Optional</span>
            </div>
            <p className="text-gray-500 mb-6">
              Set the name, role, personality, and tone of your Smart Assistant&trade;. You can skip and use our defaults &mdash; you can always change these later from Profile settings.
            </p>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assistant Name
                  </label>
                  <input
                    type="text"
                    value={agentSettings.agent_name}
                    onChange={(e) => {
                      setAgentTouched(true);
                      setAgentSettings({ ...agentSettings, agent_name: e.target.value });
                    }}
                    placeholder="Assistant"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assistant Role
                  </label>
                  <input
                    type="text"
                    value={agentSettings.agent_role}
                    onChange={(e) => {
                      setAgentTouched(true);
                      setAgentSettings({ ...agentSettings, agent_role: e.target.value });
                    }}
                    placeholder="helpful assistant"
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Personality Traits <span className="text-gray-400 font-normal">(pick up to 5)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {PERSONALITY_TRAITS.map((trait) => {
                    const isSelected = agentSettings.personality_traits.includes(trait.value);
                    const isDisabled = !isSelected && agentSettings.personality_traits.length >= 5;
                    return (
                      <button
                        key={trait.value}
                        type="button"
                        onClick={() => !isDisabled && togglePersonalityTrait(trait.value)}
                        disabled={isDisabled}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                          isSelected
                            ? 'bg-cleona-50 text-cleona-700 border-2 border-cleona-500'
                            : isDisabled
                            ? 'bg-gray-50 text-gray-300 border border-gray-200 cursor-not-allowed'
                            : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {trait.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="md:w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Response Style
                </label>
                <div className="relative">
                  <select
                    value={agentSettings.response_style}
                    onChange={(e) => {
                      setAgentTouched(true);
                      setAgentSettings({ ...agentSettings, response_style: e.target.value });
                    }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 appearance-none focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent bg-white"
                  >
                    {RESPONSE_STYLES.map((style) => (
                      <option key={style.value} value={style.value}>
                        {style.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="h-5 w-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Chat Widget Avatar */}
        {currentStep === 3 && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-semibold text-gray-900">SmartAssistant&trade; Avatar</h2>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Optional</span>
            </div>
            <p className="text-gray-500 mb-6">Upload a profile photo that will appear on your SmartAssistant&trade;. You can skip this and set it up later from Profile settings.</p>

            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${
                isDragActive
                  ? 'border-cleona-400 bg-cleona-50'
                  : 'border-gray-300 hover:border-cleona-400'
              }`}
            >
              <input {...getInputProps()} />

              {avatarPreview ? (
                <div className="flex flex-col items-center">
                  <img
                    src={avatarPreview}
                    alt="Avatar preview"
                    className="w-32 h-32 rounded-full object-cover mb-4"
                  />
                  <p className="text-sm text-gray-600">Click or drag to replace</p>
                </div>
              ) : (
                <>
                  <CloudArrowUpIcon className="h-16 w-16 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-900 font-medium">
                    Choose <span className="text-cleona-600">Avatar Image</span>
                  </p>
                  <p className="text-sm mt-2">
                    <span className="text-cleona-600">JPEG, PNG,</span> or{' '}
                    <span className="text-cleona-600">WebP</span> up to{' '}
                    <span className="text-cleona-600">5MB</span>. Recommended size:{' '}
                    <span className="text-cleona-600">200x200px</span>
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Where can the chat be used? */}
        {currentStep === 4 && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-xl font-semibold text-gray-900">Where can people use your Smart Assistant&trade;?</h2>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">Optional</span>
            </div>
            <p className="text-gray-500 mb-6">Pick where the chat is allowed to appear. You can change this later from Profile settings.</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Option A: stay inside Cleona only */}
              <button
                onClick={() => {
                  setDomainMode('specific');
                  setDomains([]);
                }}
                className={`p-6 rounded-xl border-2 text-left transition-colors ${
                  domainMode === 'specific'
                    ? 'border-cleona-500 bg-cleona-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <LockClosedIcon className={`h-5 w-5 ${domainMode === 'specific' ? 'text-cleona-600' : 'text-gray-400'}`} />
                  <span className={`font-semibold ${domainMode === 'specific' ? 'text-cleona-600' : 'text-gray-900'}`}>
                    Use SmartAssistant&trade; only here
                  </span>
                  {domainMode === 'specific' && (
                    <CheckIcon className="h-5 w-5 text-cleona-600 ml-auto" />
                  )}
                </div>
                <p className={`text-sm ${domainMode === 'specific' ? 'text-cleona-600' : 'text-gray-500'}`}>
                  Just use the chat inside your Cleona dashboard for now. Pick this if you're not ready to put it on an external website yet.
                </p>
              </button>

              {/* Option B: embed on a website */}
              <button
                onClick={() => setDomainMode('unrestricted')}
                className={`p-6 rounded-xl border-2 text-left transition-colors ${
                  domainMode === 'unrestricted'
                    ? 'border-cleona-500 bg-cleona-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <GlobeAltIcon className={`h-5 w-5 ${domainMode === 'unrestricted' ? 'text-cleona-600' : 'text-gray-400'}`} />
                  <span className={`font-semibold ${domainMode === 'unrestricted' ? 'text-cleona-600' : 'text-gray-900'}`}>
                    Put SmartAssistant&trade; on a website
                  </span>
                  {domainMode === 'unrestricted' && (
                    <CheckIcon className="h-5 w-5 text-cleona-600 ml-auto" />
                  )}
                </div>
                <p className={`text-sm ${domainMode === 'unrestricted' ? 'text-cleona-600' : 'text-gray-500'}`}>
                  Embed the chat anywhere. By default it works on any site &mdash; you can also limit it to specific websites below.
                </p>
              </button>
            </div>

            {/* Optional disclosure: only when "Put chat on a website" is picked */}
            {domainMode === 'unrestricted' && (
              <details className="bg-gray-50 rounded-xl p-6">
                <summary className="font-medium text-gray-900 cursor-pointer select-none">
                  Limit which websites can use the chat <span className="text-gray-400 font-normal">(optional)</span>
                </summary>
                <p className="text-sm text-gray-500 mt-3 mb-4">
                  By default, the chat will load on any website that has your embed code. Add specific sites below if you want to restrict it.
                </p>

                {/* Domain Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {domains.map((domain) => (
                    <span
                      key={domain}
                      className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-cleona-600"
                    >
                      {domain}
                      <button
                        onClick={() => handleRemoveDomain(domain)}
                        className="text-red-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </span>
                  ))}
                  {domains.length === 0 && (
                    <span className="text-sm text-gray-400">No restrictions yet &mdash; chat will work on any site.</span>
                  )}
                </div>

                {/* Add Domain Input */}
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddDomain()}
                    placeholder="e.g., example.com or app.example.com"
                    className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cleona-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleAddDomain}
                    disabled={!newDomain.trim()}
                    className="flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PlusIcon className="h-5 w-5" />
                    Add
                  </button>
                </div>
              </details>
            )}
          </div>
        )}

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between gap-4 mt-8">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handleSkip}
                disabled={updateMutation.isLoading}
                className="px-4 py-3 text-cleona-600 hover:text-cleona-700 transition-colors font-medium disabled:opacity-50"
              >
                Skip for later
              </button>
            )}
          </div>
          <div className="flex gap-4">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="px-8 py-3 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
              >
                Back
              </button>
            )}
            {currentStep < TOTAL_STEPS ? (
              <button
                onClick={handleNext}
                className="px-8 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={updateMutation.isLoading || agentMutation.isLoading}
                className="px-8 py-3 bg-cleona-600 text-white rounded-lg hover:bg-cleona-700 transition-colors font-medium disabled:opacity-50"
              >
                {updateMutation.isLoading || agentMutation.isLoading ? 'Saving...' : 'Finish Setup'}
              </button>
            )}
          </div>
        </div>
      </div>

      <SetupCompleteModal
        isOpen={showSuccessModal}
        onGoToDashboard={() => navigate('/admin')}
        onTestAssistant={() => navigate('/admin/chat')}
        onViewDeployment={() => navigate('/admin/widgets')}
      />
    </div>
  );
}
